
import axios from 'axios'
import {
  defineComponent,
  computed,
  reactive,
  ref,
  onMounted,
  SetupContext
} from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import smoothscroll from 'smoothscroll-polyfill'
import EventSimpleBox from '@/components/block/EventSimpleBox.vue'
import FormFieldset from '@/components/block/FormFieldset.vue'
import FormGroup from '@/components/block/FormGroup.vue'
import FormInputField from '@/components/block/FormInputField.vue'
import FormRadioField from '@/components/block/FormRadioField.vue'
import FormSelectField from '@/components/block/FormSelectField.vue'
import GoalButtons from '@/components/block/GoalButtons.vue'
import CheckboxButtons from '@/components/block/FormCheckboxButtons.vue'
import FormBirthDate from '@/components/block/FormBirthDate.vue'
import Alert from '@/components/elements/Alert.vue'
import HeadingLv3 from '@/components/elements/HeadingLv3.vue'
import Button from '@/components/elements/Button.vue'
import { applyValidation } from '@/utils/validation'

export default defineComponent({
  name: 'RegisterEventsProfile',

  components: {
    EventSimpleBox,
    FormFieldset,
    FormGroup,
    FormInputField,
    FormRadioField,
    FormSelectField,
    GoalButtons,
    CheckboxButtons,
    FormBirthDate,
    Alert,
    HeadingLv3,
    Button
  },

  props: {
    data: Object,
    state: {
      type: Object,
      require: true
    }
  },

  setup(props: any, context: SetupContext) {
    const store = useStore()
    const router = useRouter()

    // 入力ページ経由じゃない場合は弾く
    // if (!props.state.isLandingInput) {
    //   router.replace('/500')
    // }

    const visibleErrorMessage = ref<boolean>(false)
    const formLineMessage = ref<boolean>(false)

    // ------------------------------------------------------
    // データ取得・セット
    // ------------------------------------------------------
    interface RadioObject {
      label: string
      value: string
      id: string
    }
    interface SelectObject {
      label: string
      value: string
      selected: boolean
    }
    const selectDefault = {
      label: '選択してください',
      value: '',
      selected: false
    }

    // 性別をセット
    const genders: Array<RadioObject> = reactive([])
    store.state.master.gender.forEach((el: any) => {
      if (el.opt_id === '0') return

      const item: RadioObject = {
        label: el.option_name,
        value: el.opt_id,
        id: 'gender' + el.opt_id
      }
      genders.push(item)
    })

    // 都道府県をセット
    const prefectures: Array<SelectObject> = reactive([selectDefault])
    const prefectures_vacation: Array<SelectObject> = reactive([selectDefault])
    const selectedPref = props.data.userProfile.prefecture
      ? props.data.userProfile.prefecture
      : 0
    const selectedPrefVacation = props.data.userProfile.prefecture_vacation
      ? props.data.userProfile.prefecture_vacation
      : 0

    store.state.master.prefectures.forEach((el: any) => {
      if (el.opt_id === '0') return

      const item: SelectObject = {
        label: el.option_name,
        value: el.opt_id,
        selected: selectedPref === el.opt_id ? true : false
      }
      const item2: SelectObject = {
        label: el.option_name,
        value: el.opt_id,
        selected: selectedPrefVacation === el.opt_id ? true : false
      }
      prefectures.push(item)
      prefectures_vacation.push(item2)
    })

    // 大学をセット
    const universities: Array<SelectObject> = reactive([selectDefault])
    const selectedSchool = props.data.userProfile.school_id
      ? props.data.userProfile.school_id
      : 0

    store.state.master.university.forEach((el: any) => {
      if (el.opt_id === '0') return

      const item: SelectObject = {
        label: el.option_name,
        value: el.opt_id,
        selected: selectedSchool === el.opt_id ? true : false
      }
      universities.push(item)
    })

    // 希望科目をセット
    const hoped_departments01: Array<SelectObject> = reactive([selectDefault])
    const hoped_departments02: Array<SelectObject> = reactive([selectDefault])
    const hoped_departments03: Array<SelectObject> = reactive([selectDefault])
    const selectedHd01 = props.data.userProfile.hoped_departments[0]
      ? props.data.userProfile.hoped_departments[0]
      : 0
    const selectedHd02 = props.data.userProfile.hoped_departments[1]
      ? props.data.userProfile.hoped_departments[1]
      : 0
    const selectedHd03 = props.data.userProfile.hoped_departments[2]
      ? props.data.userProfile.hoped_departments[2]
      : 0

    store.state.master.departments.forEach((el: any) => {
      if (el.opt_id === '0') return

      const item01: SelectObject = {
        label: el.option_name,
        value: el.opt_id,
        selected: selectedHd01 === el.opt_id ? true : false
      }
      const item02: SelectObject = {
        label: el.option_name,
        value: el.opt_id,
        selected: selectedHd02 === el.opt_id ? true : false
      }
      const item03: SelectObject = {
        label: el.option_name,
        value: el.opt_id,
        selected: selectedHd03 === el.opt_id ? true : false
      }
      hoped_departments01.push(item01)
      hoped_departments02.push(item02)
      hoped_departments03.push(item03)
    })

    // 年をセット
    const setYear = (type: string) => {
      const years: Array<SelectObject> = []
      const endSettingValue = type === 'graduate' ? 7 : 0
      const thisYear = new Date().getFullYear()
      const startYear = thisYear - 20
      const endYear = thisYear + endSettingValue

      let selectedYear = 0
      years.push({
        label: '選択してください',
        value: '',
        selected: false
      })
      if (type === 'graduate') {
        selectedYear = props.data.userProfile.graduate_year
          ? Number(props.data.userProfile.graduate_year)
          : 0
      } else {
        selectedYear = props.data.userProfile.licensing_year
          ? Number(props.data.userProfile.licensing_year)
          : 0
      }

      for (let i = endYear; i >= startYear; i--) {
        years.push({
          label: i + '年',
          value: i + '',
          selected: i === selectedYear ? true : false
        })
      }

      return years
    }

    let isBirthDayInputCompleteOnce = false

    if (props.data.userProfile.birthday) {
      const arr = props.data.userProfile.birthday.split('/')
      if (arr[0] && arr[1] && arr[2]) isBirthDayInputCompleteOnce = true
    }

    // ------------------------------------------------------
    // バリデーション設定
    // ------------------------------------------------------
    interface ValidationsObject {
      [key: string]: any
    }

    const validations: ValidationsObject = reactive({
      name_sei: {
        rules: ['required', 'max:500'],
        isError: false,
        errorMessage: ''
      },
      name_mei: {
        rules: ['required', 'max:500'],
        isError: false,
        errorMessage: ''
      },
      kana_sei: {
        rules: ['required', 'max:500', 'kana'],
        isError: false,
        errorMessage: ''
      },
      kana_mei: {
        rules: ['required', 'max:500', 'kana'],
        isError: false,
        errorMessage: ''
      },
      birthday: {
        rules: ['required', 'date'],
        isError: false,
        errorMessage: ''
      },
      gender: {
        rules: ['required', 'select'],
        isError: false,
        errorMessage: ''
      },
      email: {
        rules: ['required', 'max:500', 'email'],
        isError: false,
        errorMessage: ''
      },
      email_confirmation: {
        rules: ['required', 'matchEmail'],
        isError: false,
        errorMessage: ''
      },
      zip: {
        rules: ['required', 'zip'],
        isError: false,
        errorMessage: ''
      },
      prefecture: {
        rules: ['required', 'select'],
        isError: false,
        errorMessage: ''
      },
      city: {
        rules: ['required', 'max:255'],
        isError: false,
        errorMessage: ''
      },
      town: {
        rules: ['required', 'max:255'],
        isError: false,
        errorMessage: ''
      },
      tel: {
        rules: ['required', 'max:500', 'hankakueisu'],
        isError: false,
        errorMessage: ''
      },
      zip_vacation: {
        rules: ['zip'],
        isError: false,
        errorMessage: ''
      },
      prefecture_vacation: {
        rules: ['select'],
        isError: false,
        errorMessage: ''
      },
      city_vacation: {
        rules: ['max:255'],
        isError: false,
        errorMessage: ''
      },
      town_vacation: {
        rules: ['max:255'],
        isError: false,
        errorMessage: ''
      },
      school_id: {
        rules: ['required', 'select'],
        isError: false,
        errorMessage: ''
      },
      graduate_year: {
        rules: ['required', 'select'],
        isError: false,
        errorMessage: ''
      },
      licensing_year: {
        rules: ['required', 'select'],
        isError: false,
        errorMessage: ''
      },
      work_place: {
        rules: ['required', 'max:255'],
        isError: false,
        errorMessage: ''
      },
      hoped_departments01: {
        rules: ['select'],
        isError: false,
        errorMessage: ''
      },
      hoped_departments02: {
        rules: ['select'],
        isError: false,
        errorMessage: ''
      },
      hoped_departments03: {
        rules: ['select'],
        isError: false,
        errorMessage: ''
      },
      password: {
        rules: ['password'],
        isError: false,
        errorMessage: ''
      },
      password_confirmation: {
        rules: ['matchPassword'],
        isError: false,
        errorMessage: ''
      },
      socialplus_id: {
        rules: ['max:255'],
        isError: false,
        errorMessage: ''
      }
    })

    // ------------------------------------------------------
    // 値の入力・アップデート
    // ------------------------------------------------------

    // Eメール確認用
    const checkConfirmField = (obj: Record<string, string>, value: string) => {
      const rules = validations[obj.name].rules

      const arr: Array<string> = []

      for (let i = 0; i < rules.length; i++) {
        if (rules[i] === 'matchEmail') {
          arr.push('matchEmail:' + obj.target)
        } else if (
          rules[i] === 'matchPassword' &&
          props.data.userProfile.socialplus_id === ''
        ) {
          arr.push('matchPassword:' + obj.target)
        } else {
          arr.push(rules[i])
        }
      }

      const result = applyValidation(arr, value)

      validations[obj.name].isError = result.isError
      validations[obj.name].errorMessage = result.message

      context.emit('update-profile', obj.name, value)
    }

    const changeMessage = (name: string, value: string) => {
      context.emit('update-profile', name, value)

      if (name === 'birthday' && value) {
        const arr = value.split('/')
        // 入力値がどこか空だったら
        if (!arr[0] || !arr[1] || !arr[2]) {
          if (isBirthDayInputCompleteOnce) {
            value = '' // 一度全て入力している場合は必須エラーになるように
          } else {
            return // 一度も入力完了していない場合はエラーにしない
          }
        }
        // 全て入力完了したらフラグをtrueに
        if (arr[0] && arr[1] && arr[2]) {
          isBirthDayInputCompleteOnce = true
        }
      }

      const result = applyValidation(validations[name].rules, value)

      validations[name].isError = result.isError
      validations[name].errorMessage = result.message

      if (
        name === 'email' &&
        props.data.userProfile.email_confirmation &&
        props.data.userProfile.socialplus_id === ''
      ) {
        checkConfirmField(
          { name: 'email_confirmation', target: props.data.userProfile.email },
          props.data.userProfile.email_confirmation
        )
      }
      if (
        name === 'password' &&
        props.data.userProfile.password_confirmation &&
        props.data.userProfile.socialplus_id === ''
      ) {
        checkConfirmField(
          {
            name: 'password_confirmation',
            target: props.data.userProfile.password
          },
          props.data.userProfile.password_confirmation
        )
      }
    }

    // 郵便番号から住所取得
    const getAddressFromZipCode = (name: string, value: string) => {
      const zip1 = value.substr(0, 3)
      const zip2 = value.substr(3, 4)

      axios
        .get(
          `https://madefor.github.io/postal-code-api/api/v1/${zip1}/${zip2}.json`
        )
        .then((res) => {
          let selectedPref = 0

          for (let i = 0; i < prefectures.length; i++) {
            if (
              Number(res.data.data[0].prefcode) === Number(prefectures[i].value)
            ) {
              selectedPref = i
            }
          }

          if (name === 'zip') {
            prefectures[selectedPref].selected = true
            changeMessage('prefecture', prefectures[selectedPref].value)
            changeMessage('city', res.data.data[0].ja.address1)
            changeMessage('town', res.data.data[0].ja.address2)
          }
          if (name === 'zip_vacation') {
            prefectures_vacation[selectedPref].selected = true
            changeMessage(
              'prefecture_vacation',
              prefectures_vacation[selectedPref].value
            )
            changeMessage('city_vacation', res.data.data[0].ja.address1)
            changeMessage('town_vacation', res.data.data[0].ja.address2)
          }
        })
    }

    const inputZip = (name: string, value: string) => {
      changeMessage(name, value)

      if (/^[0-9]{7}$/.test(value)) {
        getAddressFromZipCode(name, value)
      }
    }

    const changeVacationAddressState = (ev: Record<string, boolean>) => {
      context.emit('update-profile', 'isEqualHolidayAddress', ev.checked)
      validations.zip_vacation.rules = ev.checked
        ? ['zip']
        : ['required', 'zip']
      validations.prefecture_vacation.rules = ev.checked
        ? ['select']
        : ['required', 'select']
      validations.city_vacation.rules = ev.checked
        ? ['max:255']
        : ['required', 'max:255']
      validations.town_vacation.rules = ev.checked
        ? ['max:255']
        : ['required', 'max:255']
    }

    // ------------------------------------------------------
    // 確認ボタンの実装と制御
    // ------------------------------------------------------

    // 確認ボタンの活性・非活性制御
    const isDisabledNextBtn = computed(() => {
      if (
        props.data.userProfile.name_sei === '' ||
        props.data.userProfile.name_mei === '' ||
        props.data.userProfile.kana_sei === '' ||
        props.data.userProfile.kana_mei === '' ||
        props.data.userProfile.gender === '' ||
        props.data.userProfile.birthday === '' ||
        props.data.userProfile.email === '' ||
        props.data.userProfile.email_confirmation === '' ||
        props.data.userProfile.school_id === '' ||
        props.data.userProfile.graduate_year === '' ||
        (props.data.userProfile.password === '' &&
          props.data.userProfile.socialplus_id === '') ||
        (props.data.userProfile.password_confirmation === '' &&
          props.data.userProfile.socialplus_id === '')
      ) {
        return true
      }

      if (props.data.userProfile.birthday) {
        const arr = props.data.userProfile.birthday.split('/')
        if (!arr[0] || !arr[1] || !arr[2]) return true
      }

      if (
        props.data.memberType === '2' &&
        (props.data.userProfile.licensing_year === '' ||
          props.data.userProfile.work_place === '')
      )
        return true

      const arr: Array<string> = Object.keys(validations)

      for (let i = 0; i < arr.length; i++) {
        if (validations[arr[i]].isError) return true
      }

      return false
    })

    const confirm = async () => {
      const fieldArr = Object.keys(validations)

      fieldArr.forEach((field: string) => {
        if (props.data.memberType === '1') {
          if (
            field === 'licensing_year' ||
            field === 'work_place' ||
            field === 'hoped_departments01' ||
            field === 'hoped_departments02' ||
            field === 'hoped_departments03'
          )
            return
        }

        let value = props.data.userProfile[field]
        let ruleArr: Array<string> = []

        if (
          field === 'email_confirmation' ||
          field === 'password_confirmation'
        ) {
          const rules = validations[field].rules
          for (let i = 0; i < rules.length; i++) {
            if (rules[i] === 'matchEmail') {
              ruleArr.push('matchEmail:' + props.data.userProfile['email'])
            } else if (
              rules[i] === 'matchPassword' &&
              props.data.userProfile.socialplus_id === ''
            ) {
              ruleArr.push(
                'matchPassword:' + props.data.userProfile['password']
              )
            } else {
              ruleArr.push(rules[i])
            }
          }
        } else {
          ruleArr = validations[field].rules
        }

        if (field === 'hoped_departments01')
          value = props.data.userProfile['hoped_departments'][0]
        if (field === 'hoped_departments02')
          value = props.data.userProfile['hoped_departments'][1]
        if (field === 'hoped_departments03')
          value = props.data.userProfile['hoped_departments'][2]

        const result = applyValidation(ruleArr, value)

        validations[field].isError = result.isError
        validations[field].errorMessage = result.message
      })

      console.log(fieldArr)
      const hasError = fieldArr.some((field: string) => {
        // NOTE: エラーになったフィールドだけconsole.errorに出力
        if (validations[field].isError) {
          console.error(field)
        }
        return validations[field].isError
      })

      if (hasError) {
        visibleErrorMessage.value = true
        smoothscroll.polyfill()
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
        return
      }

      const csrf = ref(
        document
          .querySelector('meta[name="csrf-token"]')!
          .getAttribute('content')
      )

      store.commit('showLoading')

      try {
        const response = await axios.post('/api/checkRegister', {
          member_type: props.data.memberType,
          name_sei: props.data.userProfile.name_sei,
          name_mei: props.data.userProfile.name_sei,
          kana_sei: props.data.userProfile.kana_sei,
          kana_mei: props.data.userProfile.kana_mei,
          gender: props.data.userProfile.gender,
          birthday: props.data.userProfile.birthday,
          email: props.data.userProfile.email,
          email_confirmation: props.data.userProfile.email_confirmation,
          zip: props.data.userProfile.zip,
          prefecture: props.data.userProfile.prefecture,
          city: props.data.userProfile.city,
          town: props.data.userProfile.town,
          tel: props.data.userProfile.tel,
          zip_vacation: props.data.userProfile.zip_vacation,
          prefecture_vacation: props.data.userProfile.prefecture_vacation,
          city_vacation: props.data.userProfile.city_vacation,
          town_vacation: props.data.userProfile.town_vacation,
          school_id: props.data.userProfile.school_id,
          graduate_year: props.data.userProfile.graduate_year,
          licensing_year: props.data.userProfile.licensing_year,
          work_place: props.data.userProfile.work_place,
          hoped_departments: props.data.userProfile.hoped_departments,
          password: props.data.userProfile.password,
          // eslint-disable-next-line
          password_confirmation: props.data.userProfile.password_confirmation,
          socialplus_id: props.data.userProfile.socialplus_id,
          _token: csrf,
          event_id: props.data.event_id,
          holding_type: store.state.registerEvent.holding_type,
          doctor_regist_flg: props.data.doctor_regist_flg
        })
        store.commit('closeLoading')
        router.push(`/register/events/confirm/?event_id=${props.data.event_id}`)
      } catch (errors) {
        store.commit('closeLoading')
        if (errors.response.status === 429) {
          validations['email'].isError = true
          validations['email'].errorMessage = errors.response.data.errors[
            'email'
          ].join()
        } else if (errors.response.data) {
          const errorInfo = errors.response.data
          Object.keys(errorInfo).forEach((key) => {
            if (validations[key]) {
              validations[key].isError = true
              validations[key].errorMessage = errorInfo[key].join()
            }
          })
        }
        visibleErrorMessage.value = true
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
        alert('入力内容に誤りがあるので確認してください')
      }
    }

    onMounted(async () => {
      context.emit('update-flow', 3)

      const params = new URLSearchParams(location.search)
      const token = params.get('token')

      if (token == null) return

      // tokenがある(Lineから戻ってきたとき)の処理
      formLineMessage.value = true

      try {
        const result = await axios.get(
          `/api/v1/socialplus_account?token=${token}`
        )

        console.info(result)
        Object.keys(result.data).forEach((key) => {
          changeMessage(key, result.data[key])
          if (key === 'prefecture' && result.data[key]) {
            if (prefectures[result.data[key]])
              prefectures[result.data[key]].selected = true
          }
        })
      } catch (error) {
        console.error(error)
      }

      // NOTE: これまでcookieに保存していたデータの処理
      const userSelection = JSON.parse(
        localStorage.getItem('userSelection') || ''
      )
      console.log('userSelection', userSelection)
      context.emit('update-keywords-line', userSelection.hopeKeywords)
      context.emit('update-prefectures-line', userSelection.hopePrefectures)
      context.emit('update-member-type', userSelection.hopeMemberType)
      context.emit('set-invitation-code', userSelection.invitationCode)
      context.emit('set-event-id', userSelection.event_id)

      const registerEvent = JSON.parse(
        localStorage.getItem('registerEvent') || ''
      )
      console.log('registerEvent', registerEvent)
      store.commit('setRegisterEvent', registerEvent)
    })

    const goBack = () => {
      router.push(`/register/events/select?event_id=${props.data.event_id}`)
    }

    return {
      visibleErrorMessage,
      formLineMessage,
      genders,
      changeMessage,
      checkConfirmField,
      confirm,
      setYear,
      inputZip,
      changeVacationAddressState,
      prefectures,
      prefectures_vacation,
      universities,
      hoped_departments01,
      hoped_departments02,
      hoped_departments03,
      validations,
      isDisabledNextBtn,
      goBack
    }
  }
})
